import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheck } from '#src/atoms/icons/system/iconCheck.ts';
import { iconX } from '#src/atoms/icons/system/iconX.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelM, LabelS } from '#src/atoms/typography/index.tsx';
import { TextField } from '#src/components/form/Form/index.tsx';

import { useDebounce } from 'react-use';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { useEnterCode } from '../EnterCodeButton/useEnterCode.ts';

const DEBOUNCE_MS = 600;

type ValidationState = 'idle' | 'loading' | 'valid' | 'invalid';

const InlineCard = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const InlineCardHeader = styled(Horizontal)`
  justify-content: space-between;
  align-items: center;
`;

const InlineTextField = styled(TextField)`
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  background-color: rgba(255, 255, 255, 0.08);

  input {
    color: var(--c-white);
    background-color: transparent;
  }

  input::placeholder {
    opacity: 0.5;
  }
`;

export const EnterCodeInline = () => {
  const { formatMessage } = useIntl();
  const {
    inputValue,
    setInputValue,
    error,
    validateAndApplyCode,
    shouldHideCodeInput,
  } = useEnterCode();
  const [validationState, setValidationState] =
    useState<ValidationState>('idle');

  useDebounce(
    async () => {
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      const valid = await validateAndApplyCode();
      setValidationState(valid ? 'valid' : 'invalid');
    },
    DEBOUNCE_MS,
    [inputValue]
  );

  if (shouldHideCodeInput) return null;

  const adornment =
    validationState === 'valid' ? (
      <FontAwesomeIcon icon={iconCheck} color="var(--c-green-300)" />
    ) : validationState === 'invalid' ? (
      <FontAwesomeIcon icon={iconX} color="var(--c-red-400)" />
    ) : validationState === 'loading' ? (
      <LoadingIndicator
        smaller
        style={{
          width: 'unset',
        }}
      />
    ) : null;

  return (
    <InlineCard>
      <InlineCardHeader>
        <LabelS as="span" color="var(--c-white)">
          <FormattedMessage defaultMessage="Add a code" />
        </LabelS>
      </InlineCardHeader>
      <InlineTextField
        value={inputValue}
        onChange={e => {
          setValidationState('loading');
          setInputValue(e.target.value.toUpperCase());
        }}
        name="code"
        placeholder={formatMessage({ defaultMessage: 'Enter your code' })}
        endAdornment={adornment}
      />
      {validationState === 'invalid' && !!error && (
        <LabelM color="var(--c-red-400)">{error}</LabelM>
      )}
    </InlineCard>
  );
};
